import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { InjectedFormProps } from 'redux-form';
import { Field } from 'redux-form';

import type CrewTypeRequestModel from 'ab-requestModels/crewType.requestModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import ColorPicker from 'af-fields/ColorPicker';
import Input from 'af-fields/Input';

import Checkbox from 'af-components/Controls/Checkbox';
import MultipleOptionsButton from 'af-components/MultipleOptionsButton';
import RectangleButton from 'af-components/MultipleOptionsButton/RectangleButton';
import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

interface OwnProps extends InjectedFormProps<CrewTypeRequestModel> {
	onSubmit: (form: CrewTypeRequestModel) => void;
	close: () => void;
}

interface State {
	focused: boolean;
	enableForeman: boolean;
}

const TOOLTIP = <>
	<b>Crew Lead</b><br />
	Mark as Crew Lead Crew Type
</>;

type Props = OwnProps & ConnectedProps<typeof connector>;
class CrewTypeForm extends React.PureComponent<Props> {
	state: State = {
		focused: false,
		enableForeman: this.props.initialValues?.enableForeman ?? false,
	};

	handleKeyDown = (e) => {
		const { handleSubmit, onSubmit, submitting } = this.props;
		if (e.key === 'Enter' && e.shiftKey === false) {
			e.preventDefault();
			if (!submitting) {
				handleSubmit(onSubmit)(e);
			}
		}
	};

	handleForemanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const enableForeman = event.target.checked;

		this.setState(() => ({ enableForeman }));
		this.props.change('enableForeman', enableForeman);
	};

	close = () => this.props.close();

	toggleFocus = () => this.setState(() => ({ focused: !this.state.focused }));

	onColorFocus = () => this.props.touch('color');

	render() {
		const { handleSubmit, onSubmit, canManageForeman } = this.props;
		const { focused, enableForeman } = this.state;

		let wrapperClassName = 'color-picker__wrapper color-picker__wrapper--open';
		wrapperClassName = focused ? `${wrapperClassName} color-picker__wrapper--focused` : wrapperClassName;

		return (
			<form className="color-picker__form">
				<Field
					autoFocus={true}
					centered={true}
					component={Input}
					hideErrorText={true}
					isStandalone={true}
					name="name"
					onBlur={this.toggleFocus}
					onFocus={this.toggleFocus}
					onKeyDown={this.handleKeyDown}
					placeholder="Enter Crew Type"
					type="text"
				/>
				<div className={wrapperClassName}>
					<Field
						component={ColorPicker}
						name="color"
						onFocus={this.onColorFocus}
					/>
				</div>
				{
					canManageForeman && <div className={styles.foremanToggle}>
						<Tooltip message={TOOLTIP}>
							<Field
								component={Checkbox}
								handleChange={this.handleForemanChange}
								isChecked={enableForeman}
								name="enableForeman"
							/>
						</Tooltip>
					</div>
				}
				<MultipleOptionsButton isLeftFlat={true}>
					<RectangleButton
						action={handleSubmit(onSubmit)}
						isLeftOpen={true}
						isSquare={true}
						label={
							<Tooltip message="Submit">
								<span className="icon-check" />
							</Tooltip>
						}
					/>
					<RectangleButton
						action={this.close}
						isSquare={true}
						label={
							<Tooltip message="Close">
								<span className="icon-close" />
							</Tooltip>
						}
					/>
				</MultipleOptionsButton>
			</form>
		);
	}
}

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const canManageForeman = isAllowed(
		PagePermissions.COMPANY.RESOURCES.CREW_FOREMAN,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		canManageForeman,
	};
}

const connector = connect(mapStateToProps);

export default connector(CrewTypeForm);
